// 3. 가맹점 관리 > 3.1 가맹점 목록 > 3.1.2 가맹점 수정

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { observer } from "mobx-react";
import { useForm, Controller } from "react-hook-form";

import { rootStore } from "@/mobx/store";
import fonts from "@/design/typography";
import colors from "@/design/colors";
import { Page_settings } from "@/config/page_settings";
import useDebounce from "@/hooks/useDebounce";
import { ChevronLeft } from "@/components/icons/chevron";
import RowHeaderTable from "@/components/table/RowHeaderTable";
import ImageCircle from "@/components/image_circle";
import Button from "@/components/button";

import { useWindowSize } from "@/hooks/useWindowSize";
import { deviceWidth } from "@/design/defaultTheme";
import { useClickOutsideCloseModal } from "@/hooks/useClickOutsideCloseElement";
import TextField from "@/components/text_field";
import SelectBox from "@/components/select_box";
import BusinessTypeCodeModal from "./Modal/Add_franchise_businessTypeCode_modal";
//import { businessTypeCode } from "@/type/payments/businessTypeCode";

/* **********************************
 * TYPES
 * 타입 정의
 ********************************** */
interface FranchiseForm {
  platformInfoId: string;
  catId: string;
  storeName: string;
  bizRegNum: string;
  taxType: string;
  taxRate: string;
  serviceChargeYn: string;
  serviceChargeRate: string;
  ceoName: string;
  businessTypeCode: string;
  telNum: string;
  emailAddr: string;
  storeCode: string;
}

const EditFranchise = observer(() => {
  /* **********************************
   * GLOBAL STATE
   * 전역 상태 및 전역 변수
   ********************************** */
  const context: any = useContext(Page_settings);
  const token = rootStore.getMe?.accessToken;
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const isMobile = width < deviceWidth.mobile;
  const id = parseInt(useParams().id);

  /* **********************************
   * LOCAL STATE
   * 컴포넌트 내부에서 사용하는 상태
   ********************************** */
  const [isBusinessTypeCodeModalOpen, setIsBusinessTypeCodeModalOpen] =
    useState(false);
  const [selectedBusinessTypeName, setSelectedBusinessTypeName] = useState("");

  const phoneRegex = /^(010|011|016|017|018|019)\d{7,8}$/;

  // 플랫폼 리스트
  const [platformOptions, setPlatformOptions] = useState([
    // { label: "츄잉", value: "CHEWING" },
    // { label: "카리", value: "CARI" },
    // { label: "소마", value: "SOMA" },
  ]);

  const taxTypeOptions = [
    { label: "과세", value: "TAX_ON" },
    { label: "면세", value: "TAX_FREE" },
    { label: "과세면세겸업", value: "TAX_PARALLEL" },
  ];
  const serviceChargeYnOptions = [
    { label: "예", value: "Y" },
    { label: "아니오", value: "N" },
  ];

  // 초기값 정의
  const initialValues: FranchiseForm = {
    platformInfoId: "",
    catId: "",
    storeName: "",
    bizRegNum: "",
    taxType: "",
    taxRate: "0",
    serviceChargeYn: "N",
    serviceChargeRate: "0",
    ceoName: "",
    businessTypeCode: "",
    telNum: "",
    emailAddr: "",
    storeCode: "",
  };

  const [businessTypeCode, setBusinessTypeCode] = useState("");

  // const {
  //   control,
  //   handleSubmit,
  //   setValue,
  //   watch,
  //   formState: { errors, isValid },
  // } = useForm<FranchiseForm>({
  //   mode: "onChange",
  //   defaultValues: initialValues,
  // });
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<FranchiseForm>({
    mode: "onChange",
    defaultValues: initialValues,
  });

  // 폼 값 변화 감지
  const watchedValues = watch();
  const businessTypeCodeValue = watch("businessTypeCode");

  // 수정사항이 있는지 확인
  const hasChanges = useMemo(() => {
    return Object.keys(initialValues).some(
      (key) =>
        watchedValues[key as keyof FranchiseForm] !==
        initialValues[key as keyof FranchiseForm]
    );
  }, [watchedValues]);

  // 저장 버튼 활성화 조건: 유효한 폼 + 수정사항 존재
  const isSaveEnabled = isValid && hasChanges;

  /* **********************************
   * Handler
   * 이벤트 핸들러
   ********************************** */

  const onSubmit = async (data: FranchiseForm) => {
    try {
      //console.log("제출된 가맹점 정보:");
      //console.table(data);
      //수정요청
      context.post(
        `/office/updateStoreInfo`,
        {
          storeInfoId: id,
          platformInfoId: data.platformInfoId,
          catId: data.catId,
          storeName: data.storeName,
          bizRegNum: data.bizRegNum,
          taxType: data.taxType,
          taxRate: Number(data.taxRate),
          serviceChargeYn: data.serviceChargeYn,
          serviceChargeRate: Number(data.serviceChargeRate),
          ceoName: data.ceoName,
          businessTypeCode: data.businessTypeCode,
          telNum: data.telNum,
          emailAddr: data.emailAddr,
          storeCode: data.storeCode,
        },
        (response) => {
          //console.log(response);
          if (response.RESULT_CODE === "0000") {
            //
            alert("정상적으로 처리되었습니다.");
            navigate("/payment/franchise-list");
          } else {
            //실패
            alert(response.RESULT_MSG);
          }
        }
      );
    } catch (error) {
      console.error("수정 중 오류 발생:", error);
      alert("가맹점 수정에 실패했습니다. 다시 시도해주세요.");
    }
  };

  // 폼 에러 디버깅
  useEffect(() => {
    console.log("Form errors:", errors);
  }, [errors]);

  /* **********************************
   * EFFECTS
   * 컴포넌트 상태 변경 처리
   ********************************** */
  // 토큰 유무 확인
  useEffect(() => {
    if (!token) return;
  }, [token]);

  useEffect(() => {
    context.post(`/office/getPlatformCodeList`, {}, (response) => {
      console.log(response);

      const dynamicOptions = response.resultList.map((item) => ({
        label: item.platformName,
        value: item.platformInfoId,
      }));

      setPlatformOptions(dynamicOptions);
      getStoreDetail();
    });
  }, []);

  const getStoreDetail = async () => {
    if (!id) return;
    //alert(id);

    context.post(
      `/office/getStoreDetail`,
      {
        storeInfoId: id,
      },
      (response) => {
        console.log(response);

        setValue("platformInfoId", response.platformInfoId);
        setValue("catId", response.catId);
        setValue("storeName", response.storeName);
        setValue("bizRegNum", response.bizRegNum);
        setValue("ceoName", response.ceoName);
        setValue("telNum", response.telNum);
        setValue("emailAddr", response.emailAddr);
        setValue("storeCode", response.storeCode);

        setValue("taxType", response.taxType);
        setValue("taxRate", response.taxRate);
        setValue("serviceChargeYn", response.serviceChargeYn);
        setValue("serviceChargeRate", response.serviceChargeRate);

        setValue("businessTypeCode", response.businessTypeCode);

        setSelectedBusinessTypeName(response.businessTypeCode);
        setBusinessTypeCode(response.businessTypeCode);
      }
    );
  };

  /* **********************************
   * Handlers
   ********************************** */
  const handleBusinessTypeCodeSelect = (code: string, name: string) => {
    setValue("businessTypeCode", code, { shouldValidate: true });
    setSelectedBusinessTypeName(name);
  };

  /* **********************************
   * Table
   * 테이블 데이터
   ********************************** */
  const tableData = [
    {
      label: "대표 플랫폼",
      required: true,
      value: (
        <div>
          <Controller
            name="platformInfoId"
            control={control}
            rules={{ required: "필수 입력 항목입니다" }}
            render={({ field }) => (
              <SelectBox
                width="200px"
                height="36px"
                size="small"
                options={platformOptions.map((opt) => ({
                  label: opt.label,
                  onSelect: () => field.onChange(opt.value),
                }))}
                selectedOptionLabel={
                  platformOptions.find((opt) => opt.value === field.value)
                    ?.label || "플랫폼 선택"
                }
              />
            )}
          />
          {errors.platformInfoId && (
            <div
              style={{
                color: colors.warning,
                fontSize: "12px",
                marginTop: "4px",
              }}
            >
              {errors.platformInfoId.message}
            </div>
          )}
        </div>
      ),
    },
    {
      label: "온라인 CAT ID",
      required: true,
      value: (
        <div>
          <Controller
            name="catId"
            control={control}
            rules={{ required: "필수 입력 항목입니다" }}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                height="32px"
                padding="0 10px"
                placeholder="CAT ID"
                type="text"
                onChange={(e) => {
                  const onlyNumbers = e.target.value.replace(/[^0-9]/g, ""); // 숫자 외 제거
                  if (onlyNumbers.length <= 10) {
                    field.onChange(onlyNumbers);
                  }
                }}
                error={!!errors.catId}
              />
            )}
          />
          {errors.catId && (
            <div
              style={{
                color: colors.warning,
                fontSize: "12px",
                marginTop: "4px",
              }}
            >
              {errors.catId.message}
            </div>
          )}
        </div>
      ),
    },
    {
      label: "가맹점명",
      required: true,
      value: (
        <div>
          <Controller
            name="storeName"
            control={control}
            rules={{ required: "필수 입력 항목입니다" }}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                height="32px"
                padding="0 10px"
                placeholder="가맹점명"
                error={!!errors.storeName}
              />
            )}
          />
          {errors.storeName && (
            <div
              style={{
                color: colors.warning,
                fontSize: "12px",
                marginTop: "4px",
              }}
            >
              {errors.storeName.message}
            </div>
          )}
        </div>
      ),
    },
    {
      label: "사업자번호",
      required: true,
      value: (
        <div>
          <Controller
            name="bizRegNum"
            control={control}
            rules={{ required: "필수 입력 항목입니다" }}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                height="32px"
                padding="0 10px"
                placeholder="사업자번호"
                type="text"
                onChange={(e) => {
                  const onlyNumbers = e.target.value.replace(/[^0-9]/g, ""); // 숫자 외 제거
                  if (onlyNumbers.length <= 10) {
                    field.onChange(onlyNumbers);
                  }
                }}
                error={!!errors.bizRegNum}
              />
            )}
          />
          {errors.bizRegNum && (
            <div
              style={{
                color: colors.warning,
                fontSize: "12px",
                marginTop: "4px",
              }}
            >
              {errors.bizRegNum.message}
            </div>
          )}
        </div>
      ),
    },
    {
      label: "부가세",
      required: true,
      value: (
        <Controller
          name="taxType"
          control={control}
          rules={{ required: "필수 입력 항목입니다" }}
          render={({ field }) => (
            <SelectBox
              width="200px"
              height="36px"
              size="small"
              options={taxTypeOptions.map((opt) => ({
                label: opt.label,
                onSelect: () => field.onChange(opt.value),
              }))}
              selectedOptionLabel={
                taxTypeOptions.find((opt) => opt.value === field.value)
                  ?.label || "부가세 선택"
              }
            />
          )}
        />
      ),
    },
    {
      label: "부과세비율(%)",
      required: true,
      value: (
        <div>
          <Controller
            name="taxRate"
            control={control}
            rules={{ required: "필수 입력 항목입니다" }}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                height="32px"
                padding="0 10px"
                placeholder="부과세비율(%)"
                type="text"
                onChange={(e) => {
                  const onlyNumbers = e.target.value.replace(/[^0-9]/g, ""); // 숫자 외 제거
                  if (onlyNumbers.length <= 2) {
                    field.onChange(onlyNumbers);
                  }
                }}
                error={!!errors.taxRate}
              />
            )}
          />

          {errors.taxRate && (
            <div
              style={{
                color: colors.warning,
                fontSize: "12px",
                marginTop: "4px",
              }}
            >
              {errors.taxRate.message}
            </div>
          )}
        </div>
      ),
    },
    {
      label: "봉사료",
      required: true,
      value: (
        <Controller
          name="serviceChargeYn"
          control={control}
          rules={{ required: "필수 입력 항목입니다" }}
          render={({ field }) => (
            <SelectBox
              width="200px"
              height="36px"
              size="small"
              options={serviceChargeYnOptions.map((opt) => ({
                label: opt.label,
                onSelect: () => field.onChange(opt.value),
              }))}
              selectedOptionLabel={
                serviceChargeYnOptions.find((opt) => opt.value === field.value)
                  ?.label || "봉사료 선택"
              }
            />
          )}
        />
      ),
    },
    {
      label: "봉사료율(%)",
      required: true,
      value: (
        <div>
          <Controller
            name="serviceChargeRate"
            control={control}
            rules={{ required: "필수 입력 항목입니다" }}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                height="32px"
                padding="0 10px"
                type="text"
                onChange={(e) => {
                  const onlyNumbers = e.target.value.replace(/[^0-9]/g, ""); // 숫자 외 제거
                  if (onlyNumbers.length <= 2) {
                    field.onChange(onlyNumbers);
                  }
                }}
                placeholder="봉사료율(%)"
                error={!!errors.serviceChargeRate}
              />
            )}
          />
          {errors.serviceChargeRate && (
            <div
              style={{
                color: colors.warning,
                fontSize: "12px",
                marginTop: "4px",
              }}
            >
              {errors.serviceChargeRate.message}
            </div>
          )}
        </div>
      ),
    },
    {
      label: "대표자명",
      required: true,
      value: (
        <div>
          <Controller
            name="ceoName"
            control={control}
            rules={{ required: "필수 입력 항목입니다" }}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                height="32px"
                padding="0 10px"
                placeholder="대표자명"
                error={!!errors.ceoName}
              />
            )}
          />
          {errors.ceoName && (
            <div
              style={{
                color: colors.warning,
                fontSize: "12px",
                marginTop: "4px",
              }}
            >
              {errors.ceoName.message}
            </div>
          )}
        </div>
      ),
    },
    {
      label: "업태코드",
      required: true,
      value: (
        <div>
          <Controller
            name="businessTypeCode"
            control={control}
            rules={{ required: "필수 입력 항목입니다" }}
            render={({ field }) => (
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: isMobile ? "flex-start" : "center",
                  flexDirection: isMobile ? "column" : "row",
                }}
              >
                {field.value ? (
                  <>
                    <TextField
                      value={`${selectedBusinessTypeName}`}
                      size="small"
                      width={isMobile ? "100%" : "300px"}
                      height="32px"
                      padding="0 10px"
                      disabled
                    />
                    <Button
                      text="업태코드 검색"
                      size="small"
                      width="100px"
                      height="32px"
                      onClick={() => setIsBusinessTypeCodeModalOpen(true)}
                    />
                  </>
                ) : (
                  <Button
                    text="업태코드 검색"
                    size="small"
                    width="100px"
                    height="32px"
                    onClick={() => setIsBusinessTypeCodeModalOpen(true)}
                  />
                )}
              </div>
            )}
          />
          {errors.businessTypeCode && (
            <div
              style={{
                color: colors.warning,
                fontSize: "12px",
                marginTop: "4px",
              }}
            >
              {errors.businessTypeCode.message}
            </div>
          )}
        </div>
      ),
    },
    {
      label: "핸드폰",
      required: true,
      value: (
        <div>
          <Controller
            name="telNum"
            control={control}
            rules={{
              required: "필수 입력 항목입니다",
              pattern: {
                value: phoneRegex,
                message: "올바른 휴대폰 번호를 입력해주세요 (예: 01012345678)",
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                height="32px"
                padding="0 10px"
                placeholder="01012345678"
                error={!!errors.telNum}
              />
            )}
          />
          {errors.telNum && (
            <div
              style={{
                color: colors.warning,
                fontSize: "12px",
                marginTop: "4px",
              }}
            >
              {errors.telNum.message}
            </div>
          )}
        </div>
      ),
    },
    {
      label: "이메일",
      required: true,
      value: (
        <div>
          <Controller
            name="emailAddr"
            control={control}
            rules={{
              required: "필수 입력 항목입니다",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "유효한 이메일 형식이 아닙니다",
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                height="32px"
                padding="0 10px"
                placeholder="email@example.com"
                error={!!errors.emailAddr}
              />
            )}
          />
          {errors.emailAddr && (
            <div
              style={{
                color: colors.warning,
                fontSize: "12px",
                marginTop: "4px",
              }}
            >
              {errors.emailAddr.message}
            </div>
          )}
        </div>
      ),
    },
  ];

  /* **********************************
   * USEHOOK
   * 데이터 처리
   ********************************** */

  /* **********************************
   * HOOKS
   ********************************** */

  /* **********************************
   * Element
   ********************************** */

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Container>
          <section
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "48px",
            }}
          >
            {/* 타이틀 */}
            <span
              style={{
                ...fonts.title2SemiBold,
                color: colors.gray900,
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                marginTop: "2px",
              }}
              onClick={() => {
                navigate("/payment/franchise-list");
              }}
            >
              <ChevronLeft
                style={{ width: "24px", height: "31px", marginRight: "8px" }}
              />
              <Title>가맹점 수정</Title>
            </span>
          </section>

          {/* 가맹점 정보 */}
          <section
            style={{
              marginBottom: "10px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <SubTitle></SubTitle>
            <div style={{ display: "flex", gap: "10px" }}>
              <Button
                text="취소"
                size="small"
                width="60px"
                height="32px"
                buttonColor={colors.gray400}
                textColor={colors.gray50}
                customFontSize={isMobile ? "11px" : "13px"}
                style={{ padding: "0 10px" }}
                onClick={() => {
                  navigate(`/payment/franchise-detail/${id}`);
                }}
              />
              <Button
                text="저장"
                size="small"
                width="60px"
                height="32px"
                buttonColor={colors.blue600}
                textColor={colors.gray50}
                customFontSize={isMobile ? "11px" : "13px"}
                style={{ padding: "0 10px" }}
                type="submit"
                disabled={!isSaveEnabled}
                onClick={() => {}}
              />
            </div>
          </section>
          <section>
            <RowHeaderTable data={tableData} />
          </section>
        </Container>
      </form>

      {/* 업태코드 선택 모달 */}
      {isBusinessTypeCodeModalOpen && (
        <BusinessTypeCodeModal
          onClose={() => setIsBusinessTypeCodeModalOpen(false)}
          onSelect={handleBusinessTypeCodeSelect}
          selectedBusinessCode={businessTypeCode}
        />
      )}
    </>
  );
});

/* **********************************
 * Style
 * 스타일 처리
 ********************************** */
const Container = styled.div`
  width: 100%;
  height: 100%;
  padding: 24px 40px 40px 40px;
  overflow-y: auto;

  ${({ theme }) => theme.device.mobile} {
    padding: 10px 20px 20px 20px;
  }
`;

const Title = styled.span`
  font-family: ${fonts.title2SemiBold.fontFamily};
  font-size: ${fonts.title2SemiBold.fontSize};
  line-height: ${fonts.title2SemiBold.lineHeight};
  color: ${colors.gray900};

  ${({ theme }) => theme.device.mobile} {
    font-size: 1rem;
  }
`;

const SubTitle = styled.span`
  font-family: ${fonts.body2SemiBold.fontFamily};
  font-size: ${fonts.body2SemiBold.fontSize};
  line-height: ${fonts.body2SemiBold.lineHeight};
  color: ${colors.gray900};

  ${({ theme }) => theme.device.mobile} {
    font-size: 0.8rem;
  }
`;

export default EditFranchise;
